/**
 * Leveranciersoverzicht (23 okt 2026): freelancers en bedrijven voor PL en management.
 * Alleen lezen — bewerken en toevoegen gebeurt voorlopig in Airtable (knoppen linken
 * daarheen). Eén rustige tabel met zoeken en filters, details in een paneel rechts.
 * Identiek op develop en main.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, Drawer, IconButton,
  Link, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { useUserRole } from '../../contexts/UserRoleContext';
import LeverancierToevoegen from './LeverancierToevoegen';
import {
  LEEG_FILTER, MIN_SCORES, alsLijst, datumKort, filterLeveranciers, gearchiveerdeTreffers, kwartaalTekst,
  scoreTekst, tariefTekst,
} from './overzichtLogica';

const INK = { tekst: '#2d3335', zacht: '#556071', stil: '#8a94a3', lijn: '#e5e8ec', vlak: '#f6f7f9' };
const STER = '#b88a1b';
const API = `${BACKEND_URL}/Api/leveranciers/overzicht`;

async function haal(url, wat) {
  return leesJson(await authFetch(url), wat);
}

function Extern({ href, children }) {
  return (
    <Button component="a" href={href} target="_blank" rel="noopener noreferrer" size="small"
      endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
      {children}
    </Button>
  );
}

function Regel({ label, children }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 1, py: 0.5 }}>
      <Typography sx={{ fontSize: 13, color: INK.zacht }}>{label}</Typography>
      <Typography component="div" sx={{ fontSize: 13, color: INK.tekst, minWidth: 0, overflowWrap: 'anywhere' }}>
        {children || <span style={{ color: INK.stil }}>—</span>}
      </Typography>
    </Box>
  );
}

function Kop({ children }) {
  return <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK.zacht, letterSpacing: '0.06em', textTransform: 'uppercase', mt: 3, mb: 1 }}>{children}</Typography>;
}

function Beoordelingen({ data }) {
  const kw = data?.perKwartaal || [];
  if (!kw.length) return <Typography sx={{ fontSize: 13, color: INK.stil }}>Nog niet beoordeeld.</Typography>;
  return (
    <Box>
      {kw.map((k) => (
        <Box key={k.kwartaal} sx={{ py: 0.75, borderBottom: `1px solid ${INK.lijn}` }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK.tekst, flex: 1 }}>{kwartaalTekst(k.kwartaal)}</Typography>
            <Typography sx={{ fontSize: 13, color: INK.tekst, fontVariantNumeric: 'tabular-nums' }}>
              {scoreTekst(k.gemiddelde) || '—'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: INK.stil, width: 34, textAlign: 'right' }}>{k.aantal}×</Typography>
          </Box>
          {(k.beoordelingen || []).map((b, i) => (
            <Box key={i} sx={{ pl: 1.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: INK.zacht }}>
                {[datumKort(b.datum), b.ingevuldDoor, scoreTekst(b.score)].filter(Boolean).join(' · ')}
              </Typography>
              {b.conclusie && <Typography sx={{ fontSize: 13, color: INK.tekst, whiteSpace: 'pre-line' }}>{b.conclusie}</Typography>}
            </Box>
          ))}
        </Box>
      ))}
      {!data.metOpmerkingen && (
        <Typography sx={{ fontSize: 12, color: INK.stil, mt: 1 }}>
          Per kwartaal het gemiddelde; de losse beoordelingen en opmerkingen zijn voor beheerders.
        </Typography>
      )}
    </Box>
  );
}

function Inzet({ inzet }) {
  const t = inzet?.totaal || {};
  if (!inzet || (!t.projecten && !t.draaidagen && !t.taken && !t.grippUren)) {
    return <Typography sx={{ fontSize: 13, color: INK.stil }}>Geen inzet gevonden in draaidagen, taken of Gripp-uren.</Typography>;
  }
  const samenvatting = [
    t.projecten ? `${t.projecten} project${t.projecten === 1 ? '' : 'en'}` : null,
    t.draaidagen ? `${t.draaidagen} draaidag${t.draaidagen === 1 ? '' : 'en'}` : null,
    t.taken ? `${t.taken} ta${t.taken === 1 ? 'ak' : 'ken'}` : null,
    t.grippUren ? `${String(t.grippUren).replace('.', ',')} uur in Gripp` : null,
  ].filter(Boolean).join(' · ');
  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: INK.zacht, mb: 1 }}>{samenvatting}</Typography>
      <Table size="small" sx={{ '& td, & th': { fontSize: 12.5, px: 0.75, color: INK.tekst }, '& th': { color: INK.zacht, fontWeight: 700 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Project</TableCell>
            <TableCell align="right">Draaid.</TableCell>
            <TableCell align="right">Taken</TableCell>
            <TableCell align="right">Laatst</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(inzet.projecten || []).slice(0, 15).map((p) => (
            <TableRow key={`${p.nummer}-${p.naam}`}>
              <TableCell>
                {p.nummer && <span style={{ color: INK.zacht }}>{p.nummer} </span>}{p.naam || ''}
                {p.gepland && <span style={{ color: INK.stil }}> · gepland</span>}
              </TableCell>
              <TableCell align="right">{p.draaidagen || ''}</TableCell>
              <TableCell align="right">{p.taken || ''}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{datumKort(p.laatste)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {(inzet.draaidagen || []).length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK.zacht, mb: 0.5 }}>Draaidagen</Typography>
          {inzet.draaidagen.slice(0, 10).map((dd, i) => (
            <Typography key={`${dd.datum}-${i}`} sx={{ fontSize: 12.5, color: INK.tekst }}>
              <span style={{ color: INK.zacht, display: 'inline-block', minWidth: 92 }}>{datumKort(dd.datum)}</span>
              {dd.projectNummer && <span style={{ color: INK.zacht }}>{dd.projectNummer} </span>}{dd.naam}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Detail({ rij, onClose }) {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(null);
  useEffect(() => {
    let weg = false;
    setD(null); setFout(null);
    haal(`${API}/${encodeURIComponent(rij.id)}`, 'Leverancier laden')
      .then((x) => { if (!weg) setD(x); })
      .catch((e) => { if (!weg) setFout(e.message); });
    return () => { weg = true; };
  }, [rij.id]);
  const x = d || rij;
  return (
    <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: INK.tekst }}>
            {x.favoriet && <StarIcon sx={{ fontSize: 18, color: STER, mr: 0.5, verticalAlign: '-3px' }} titleAccess="Favoriet" />}
            {x.naam}
          </Typography>
          <Typography sx={{ fontSize: 13, color: INK.zacht }}>
            {[x.type, x.bedrijf, x.actief ? null : 'archief'].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Sluiten"><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        <Extern href={x.airtableUrl}>Bewerken in Airtable</Extern>
        {x.beoordeelUrl && <Extern href={x.beoordeelUrl}>Beoordelingsformulier</Extern>}
      </Box>
      {fout && <Alert severity="error" sx={{ mt: 2 }}>{fout}</Alert>}

      <Kop>Contact</Kop>
      <Regel label="E-mail">{x.email && <Link href={`mailto:${x.email}`}>{x.email}</Link>}</Regel>
      <Regel label="Telefoon">{x.telefoon && <Link href={`tel:${x.telefoon.replace(/\s+/g, '')}`}>{x.telefoon}</Link>}</Regel>
      <Regel label="Plaats">{[x.plaats, x.provincie].filter(Boolean).join(', ')}</Regel>
      {x.contactpersoon && <Regel label="Contactpersoon">{x.contactpersoon}</Regel>}
      {d?.freelancerMail && <Regel label="Freelancer-mail">{d.freelancerMail.adres}{d.freelancerMail.status !== 'actief' ? ` (${d.freelancerMail.status})` : ''}</Regel>}

      <Kop>Werk</Kop>
      <Regel label="Functies">{alsLijst(x.functies).join(', ')}</Regel>
      <Regel label="Specialisatie">{alsLijst(x.specialisaties).join(', ')}</Regel>
      <Regel label="Niveau">{x.niveau}</Regel>
      <Regel label="Tarief">{tariefTekst(x)}</Regel>
      <Regel label="Portfolio">{x.portfolio && <Link href={x.portfolio} target="_blank" rel="noopener noreferrer">Openen</Link>}</Regel>
      {d && (d.eigenApparatuur || d.drone) && (
        <Regel label="Extra">{[d.eigenApparatuur && 'eigen apparatuur', d.drone && 'drone'].filter(Boolean).join(', ')}</Regel>
      )}
      {d?.overeenkomst && d.overeenkomst !== 'N.v.t.' && <Regel label="Overeenkomst">{d.overeenkomst === 'Ja' ? 'Actief' : 'Geen actieve'}</Regel>}

      <Kop>Beoordelingen</Kop>
      {d ? <Beoordelingen data={d.beoordelingen} /> : !fout && <CircularProgress size={18} />}

      <Kop>Ingezet op</Kop>
      {d ? <Inzet inzet={d.inzet} /> : !fout && <CircularProgress size={18} />}
      {d?.bron === 'operationeel' && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 12, color: INK.stil }}>
            Staat alleen in Operationeel-Personen, niet in de Leveranciers-base: tarieven en beoordelingen ontbreken.
          </Typography>
        </>
      )}
    </Box>
  );
}

const Kolom = ({ children, ...rest }) => <TableCell {...rest}>{children}</TableCell>;

export default function LeveranciersOverzicht() {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(null);
  const [f, setF] = useState(LEEG_FILTER);
  const [open, setOpen] = useState(null);
  const [nieuw, setNieuw] = useState(false);
  const [toegevoegd, setToegevoegd] = useState(null);
  const { permissions } = useUserRole() || {};
  const magToevoegen = !!permissions?.leveranciers?.edit;

  const laad = useCallback(async () => {
    setFout(null);
    try { setD(await haal(API, 'Leveranciers laden')); } catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const lijst = useMemo(() => filterLeveranciers(d?.leveranciers, f), [d, f]);
  const gearchiveerd = useMemo(() => gearchiveerdeTreffers(d?.leveranciers, f), [d, f]);
  const zet = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  if (fout) return <Alert severity="error" action={<Button color="inherit" size="small" onClick={laad}>Opnieuw</Button>}>{fout}</Alert>;
  if (!d) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const veld = { size: 'small', sx: { width: 170 } };
  return (
    <>
      {d.melding && <Alert severity="info" sx={{ mb: 2 }}>{d.melding}</Alert>}
      {toegevoegd && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setToegevoegd(null)}>
          {toegevoegd.heractiveerd
            ? `${toegevoegd.naam} is weer actief in Airtable. Dat is hier zichtbaar na de volgende verversing van de lijst.`
            : `${toegevoegd.naam} staat in Airtable. Hij verschijnt hier na de volgende verversing van de lijst.`}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
        <TextField size="small" label="Zoeken" placeholder="naam, functie, plaats" value={f.zoek} onChange={zet('zoek')} sx={{ width: 220 }} />
        <TextField select label="Functie" value={f.functie} onChange={zet('functie')} {...veld}>
          <MenuItem value="">Alle functies</MenuItem>
          {(d.functies || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField select label="Specialisatie" value={f.specialisatie} onChange={zet('specialisatie')} {...veld}>
          <MenuItem value="">Alle specialisaties</MenuItem>
          {(d.specialisaties || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField select label="Soort" value={f.type} onChange={zet('type')} size="small" sx={{ width: 150 }}>
          <MenuItem value="">Alle soorten</MenuItem>
          <MenuItem value="Freelancer">Freelancers</MenuItem>
          <MenuItem value="Bedrijf">Bedrijven</MenuItem>
        </TextField>
        <TextField select label="Beoordeling" value={f.minScore} onChange={zet('minScore')} size="small" sx={{ width: 160 }}>
          {MIN_SCORES.map((m) => <MenuItem key={m.waarde} value={m.waarde}>{m.label}</MenuItem>)}
        </TextField>
        <TextField select label="Status" value={f.status} onChange={zet('status')} size="small" sx={{ width: 120 }}>
          <MenuItem value="actief">Actief</MenuItem>
          <MenuItem value="archief">Archief</MenuItem>
          <MenuItem value="alle">Alle</MenuItem>
        </TextField>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: INK.stil }}>{lijst.length} van {d.leveranciers.length}</Typography>
          {magToevoegen
            ? <Button size="small" variant="outlined" onClick={() => setNieuw(true)} sx={{ textTransform: 'none' }}>Leverancier toevoegen</Button>
            : d.nieuwUrl && <Extern href={d.nieuwUrl}>Toevoegen in Airtable</Extern>}
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& th': { color: INK.zacht, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }, '& td': { fontSize: 13, color: INK.tekst, verticalAlign: 'top' } }}>
          <TableHead>
            <TableRow>
              <Kolom>Naam</Kolom>
              <Kolom>Functies</Kolom>
              <Kolom>Plaats</Kolom>
              <Kolom>Contact</Kolom>
              <Kolom>Tarief</Kolom>
              <Kolom align="right">Beoordeling</Kolom>
              <Kolom>Laatste inzet</Kolom>
            </TableRow>
          </TableHead>
          <TableBody>
            {lijst.map((r) => (
              <TableRow key={r.id} hover tabIndex={0} onClick={() => setOpen(r)}
                onKeyDown={(e) => { if (e.key === 'Enter') setOpen(r); }} sx={{ cursor: 'pointer' }}>
                <Kolom>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    {r.favoriet && <StarIcon sx={{ fontSize: 15, color: STER }} titleAccess="Favoriet" />}
                    {r.naam}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: INK.stil }}>
                    {[r.type, r.bedrijf, r.actief ? null : 'archief'].filter(Boolean).join(' · ')}
                  </Typography>
                </Kolom>
                <Kolom sx={{ maxWidth: 260 }}>{alsLijst(r.functies).join(', ') || '—'}</Kolom>
                <Kolom>{r.plaats || '—'}</Kolom>
                <Kolom>
                  {r.email && <Box onClick={(e) => e.stopPropagation()}><Link href={`mailto:${r.email}`} sx={{ fontSize: 12.5 }}>{r.email}</Link></Box>}
                  {r.telefoon && <Typography sx={{ fontSize: 12.5, color: INK.zacht }}>{r.telefoon}</Typography>}
                  {!r.email && !r.telefoon && '—'}
                </Kolom>
                <Kolom sx={{ whiteSpace: 'nowrap' }}>{tariefTekst(r) || '—'}</Kolom>
                <Kolom align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {r.aantalBeoordelingen ? (
                    <Tooltip title={`Laatste beoordeling ${datumKort(r.laatsteBeoordeling) || 'onbekend'}${r.beoordelingStatus === 'verouderd' ? ' — ouder dan een half jaar' : ''}`}>
                      <span>{scoreTekst(r.score)} <span style={{ color: INK.stil }}>({r.aantalBeoordelingen})</span></span>
                    </Tooltip>
                  ) : <span style={{ color: INK.stil }}>—</span>}
                </Kolom>
                <Kolom sx={{ whiteSpace: 'nowrap' }}>{datumKort(r.laatsteInzet) || <span style={{ color: INK.stil }}>—</span>}</Kolom>
              </TableRow>
            ))}
            {lijst.length === 0 && (
              <TableRow><TableCell colSpan={7} sx={{ color: INK.zacht }}>
                {d.leveranciers.length ? 'Niets gevonden met deze filters.' : 'Nog geen leveranciers in de spiegel.'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {gearchiveerd > 0 && (
        <Button size="small" onClick={() => setF((x) => ({ ...x, status: 'alle' }))}
          sx={{ textTransform: 'none', color: INK.zacht, mt: 1 }}>
          {gearchiveerd} gearchiveerde treffer{gearchiveerd === 1 ? '' : 's'} — tonen
        </Button>
      )}

      {magToevoegen && (
        <LeverancierToevoegen open={nieuw} onClose={() => setNieuw(false)} specialisaties={d.specialisaties || []}
          onToegevoegd={(x) => setToegevoegd(x)} />
      )}
      <Drawer anchor="right" open={!!open} onClose={() => setOpen(null)}>
        {open && <Detail rij={open} onClose={() => setOpen(null)} />}
      </Drawer>
    </>
  );
}
